"use client";

import { IBrandViewV2Response } from "@faire/web-api/indigofair/data/IBrandViewV2Response";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { FakeAnchor } from "@faire/retailer/components/FakeAnchor";
import { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";
import {
  BrandReviewsFallback,
  LazyBrandReviewsList,
} from "@faire/retailer/views/brand/BrandReview";
import { REVIEWS_SECTION_ID } from "@faire/retailer/views/brand/consts";
import { BrandComponentLowerContainer } from "@faire/retailer/views/brand/styled";

interface IProps {
  brandReviewSummary: IReviewSummary;
  brand: IBrandViewV2Response.ILeanBrand;
}

export const BrandReviews: React.FC<IProps> = ({
  brand,
  brandReviewSummary,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const location = useLocation();
  useEffect(() => {
    if (location.hash.includes(REVIEWS_SECTION_ID)) {
      getWindow()
        ?.document.getElementById(REVIEWS_SECTION_ID)
        ?.scrollIntoView();
    }
  }, [location]);

  const { token, name, squared_image } = brand;

  return (
    <BrandComponentLowerContainer data-test-id="brand-reviews-section">
      <FakeAnchor id={REVIEWS_SECTION_ID} />
      <WhenVisibilityIsImminent
        forceRender={isPrerender()}
        customizePlaceholder={<BrandReviewsFallback />}
      >
        <Suspense fallback={<BrandReviewsFallback />}>
          <LazyBrandReviewsList
            brandReviewSummary={brandReviewSummary}
            brandToken={token!}
            brandName={name!}
            brandAvatar={squared_image}
            title={strictLocalize(
              {
                defaultMessage:
                  "{reviewsCount, plural, one {# review} other {# reviews}}",
                description: {
                  text: "number of reviews received by a brand",
                },
              },
              DEPRECATED_backwardsCompatibleValues({
                reviewsCount: brandReviewSummary.numberOfReviews,
              })
            )}
            brand={brand}
            containerName="brand-page"
            useLastDeliveredOrder
          />
        </Suspense>
      </WhenVisibilityIsImminent>
    </BrandComponentLowerContainer>
  );
};
